import type { ObjectId } from 'mongodb';

import { AdderType, BatteryType } from './Adders';
import type { AdditionalCostAdders } from './Adders';
import type { IPricingSettingsDocument } from './AppSettings';
import type { QuotePricingPlan, QuotePricingPlanResponse } from './PricingPlans';
import { FinanceType } from './ProductPricing';
import { ProgramType } from './ProgramTypes';
import { WarrantyType } from './Warranties';

export interface AnnualPricingDetails {
  year: number;
  monthlyPayment: number;
  estimatedAnnualProduction: number;
  guaranteedAnnualProduction: number;
  yearlyCost: number;
  kwhRate: number;
  batteryPayment?: number;
  totalMonthlyPayment: number;
  totalYearlyCost: number;
}

export enum QuoteStatus {
  active = 'active',
  contracted = 'contracted',
  voided = 'voided',
  preBillingCustomerAdjustment = 'preBillingCustomerAdjustment',
  amended = 'amended',
  changeOrderPending = 'changeOrderPending',
}

/** QuoteStatus values excluded from public API documentation */
export const InternalQuoteStatuses: QuoteStatus[] = [
  QuoteStatus.preBillingCustomerAdjustment,
  QuoteStatus.amended,
  QuoteStatus.changeOrderPending,
];

/** QuoteStatus values exposed in public API documentation */
export const PublicQuoteStatuses: QuoteStatus[] = Object.values(QuoteStatus).filter(
  (status) => !InternalQuoteStatuses.includes(status),
);

export enum BatteryPricingMethodOption {
  embedded = 'embedded', // Solely used for the DOE PR program.
  kwhRate = 'kwhRate',
  rawCost = 'rawCost',
}

export interface QuoteBatteryDetails {
  type: BatteryType;
  pricingMethod?: BatteryPricingMethodOption;
  manufacturer?: string;
  models?: Array<{
    modelNumber: string;
    quantity: number;
  }>;
}

export interface SolarQuoteDocument {
  id: ObjectId;
  /** Conga package that holds this quote's generated contract. */
  congaPackageId?: string;
  accountId: ObjectId;
  productId: ObjectId;
  externalReference?: string;
  type: FinanceType;
  productName: string;
  systemFirstYearProductionKwh: number;
  systemSizeKw: number;
  kwhPrice: number;
  escalationRate?: number;
  pricingSettings: IPricingSettingsDocument['value'];
  preConAdderCost?: number;
  totalSystemCost: number;
  pricePerWatt: number;
  systemPricingDetails: Array<AnnualPricingDetails>;
  totalAmountPaid: number;
  status: QuoteStatus;
  adders?: Array<AdderType>;
  priceSheetMappingId?: ObjectId;
  actualPricePerWatt: number;
  actualTotalSystemCost: number;
  additionalCostAdders?: AdditionalCostAdders;
  batteryDetails?: QuoteBatteryDetails;
  /** Present only on quotes whose terms include a pricing plan addendum. */
  pricingPlan?: QuotePricingPlan;
  globalPpwModifier?: number;
  orgEnergyCommunityModifierId?: ObjectId;
  orgHoldbackId?: ObjectId;
  orgPpwModifierId?: ObjectId;
  orgDomesticContentModifierId?: ObjectId;
  totalSystemCostWithoutDomesticContent?: number;
  pricePerWattWithoutDomesticContent?: number;
  actualTotalSystemCostWithoutDomesticContent?: number;
  actualPricePerWattWithoutDomesticContent?: number;
  domesticContentEligible?: boolean;
  // these holdback fields are internal only, so should not be added to the response schema
  standardHoldbackAmount: number;
  domesticContentHoldbackAmount: number;
  totalHoldbackAmount: number;
}

export interface SolarQuoteWithEffectivePPWs extends SolarQuoteDocument {
  effectivePPWRate?: number;
  effectivePPWRateWithoutDomesticContent?: number;
}

type SolarQuote = Omit<
  SolarQuoteDocument,
  | 'id'
  | 'accountId'
  | 'productId'
  | 'priceSheetMappingId'
  | 'orgEnergyCommunityModifierId'
  | 'orgHoldbackId'
  | 'orgPpwModifierId'
  | 'orgDomesticContentModifierId'
  | 'pricingPlan'
> & {
  id: string;
  accountId: string;
  productId: string;
  priceSheetMappingId?: string;
  orgEnergyCommunityModifierId?: string;
  orgHoldbackId?: string;
  orgPpwModifierId?: string;
  orgDomesticContentModifierId?: string;
  effectivePPWRate?: number;
  effectivePPWRateWithoutDomesticContent?: number;
  pricingPlan?: QuotePricingPlanResponse;
};

export interface CreateQuoteRequest {
  productId: string;
  externalReference?: string;
  arbitrageBatteryIncluded?: boolean;
  backupBatteryIncluded?: boolean;
  backupBatteryKwhIncluded?: boolean;
  electricalUpgradeIncluded?: boolean;
  backupBatteryCost?: number;
  backupBatteryManufacturer?: string;
  backupBatteryModelNumber?: string;
  backupBatteryQuantity?: number;
  includeDomesticContentPricing?: boolean;
}

export interface BatteryDetails {
  type: BatteryType;
  pricingMethod: BatteryPricingMethodOption;
  cost: number;
  manufacturer: string;
  models: Array<{
    modelNumber: string;
    quantity: number;
  }>;
}

export interface CreateQuoteRequestV3 {
  productId: string;
  externalReference?: string;
  electricalUpgradeIncluded?: boolean;
  includeDomesticContentPricing?: boolean;
  battery?: BatteryDetails;
}

export interface CreateDoePrQuoteRequest {
  productId: string;
  externalReference?: string;
  backupBatteryManufacturer?: string;
  backupBatteryModelNumber?: string;
  backupBatteryQuantity?: number;
  solarReadinessCost?: number;
}

export interface CreateHvacQuoteRequest {
  productId: string;
  externalReference?: string;
  totalFinancedAmount: number;
}

export interface CreateWaterHeaterQuoteRequest {
  productId: string;
  externalReference?: string;
  totalFinancedAmount: number;
}

export interface CreateGeneratorQuoteRequest {
  productId: string;
  externalReference?: string;
  totalFinancedAmount: number;
}

export interface CreateGeothermalQuoteRequest {
  productId: string;
  externalReference?: string;
  totalFinancedAmount: number;
}

export interface CreateEnergyBackupQuoteRequest {
  productId: string;
  externalReference?: string;
  totalFinancedAmount: number;
}

export interface EquipmentPricingDetails {
  year: number;
  monthlyPayment: number;
  totalMonthlyPayment: number;
  yearlyCost: number;
}
interface HVACQuoteDocument {
  id: ObjectId;
  congaPackageId?: string;
  accountId: ObjectId;
  productId: ObjectId;
  externalReference?: string;
  type: FinanceType;
  productName: string;
  escalationRate: number;
  totalSystemCost: number;
  systemPricingDetails: EquipmentPricingDetails[];
  totalAmountPaid: number;
  status: QuoteStatus;
  priceSheetMappingId?: ObjectId;
  warrantySettings?: Record<WarrantyType, ObjectId | undefined>;
  warrantyCosts?: Record<WarrantyType | 'totalCost', number>;
  endOfTermBuyoutPrice?: number;
  endOfTermBuyoutSchedule?: Array<{ month: number; amount: number }>;
}

interface WaterHeaterQuoteDocument {
  id: ObjectId;
  congaPackageId?: string;
  accountId: ObjectId;
  productId: ObjectId;
  externalReference?: string;
  type: FinanceType;
  productName: string;
  escalationRate: number;
  totalSystemCost: number;
  systemPricingDetails: EquipmentPricingDetails[];
  totalAmountPaid: number;
  status: QuoteStatus;
  priceSheetMappingId?: ObjectId;
  warrantySettings?: Partial<Record<WarrantyType, ObjectId | undefined>>;
  warrantyCosts?: Partial<Record<WarrantyType | 'totalCost', number>>;
  endOfTermBuyoutPrice?: number;
  endOfTermBuyoutSchedule?: Array<{ month: number; amount: number }>;
}

interface GeneratorQuoteDocument {
  id: ObjectId;
  congaPackageId?: string;
  accountId: ObjectId;
  productId: ObjectId;
  externalReference?: string;
  type: FinanceType;
  productName: string;
  escalationRate: number;
  totalSystemCost: number;
  systemPricingDetails: EquipmentPricingDetails[];
  totalAmountPaid: number;
  status: QuoteStatus;
  priceSheetMappingId?: ObjectId;
  warrantySettings?: Partial<Record<WarrantyType, ObjectId | undefined>>;
  warrantyCosts?: Partial<Record<WarrantyType | 'totalCost', number>>;
  endOfTermBuyoutPrice?: number;
  endOfTermBuyoutSchedule?: Array<{ month: number; amount: number }>;
}

type GeneratorQuote = Omit<GeneratorQuoteDocument, 'id' | 'accountId' | 'productId' | 'priceSheetMappingId'> & {
  id: string;
  accountId: string;
  productId: string;
  priceSheetMappingId: string;
};

type HVACQuote = Omit<HVACQuoteDocument, 'id' | 'accountId' | 'productId' | 'priceSheetMappingId'> & {
  id: string;
  accountId: string;
  productId: string;
  priceSheetMappingId: string;
};

type WaterHeaterQuote = Omit<WaterHeaterQuoteDocument, 'id' | 'accountId' | 'productId' | 'priceSheetMappingId'> & {
  id: string;
  accountId: string;
  productId: string;
  priceSheetMappingId: string;
};

interface GeothermalQuoteDocument {
  id: ObjectId;
  congaPackageId?: string;
  accountId: ObjectId;
  productId: ObjectId;
  externalReference?: string;
  type: FinanceType;
  productName: string;
  escalationRate: number;
  totalSystemCost: number;
  systemPricingDetails: EquipmentPricingDetails[];
  totalAmountPaid: number;
  status: QuoteStatus;
  priceSheetMappingId?: ObjectId;
  endOfTermBuyoutPrice?: number;
  endOfTermBuyoutSchedule?: Array<{ month: number; amount: number }>;
  orgEnergyCommunityModifierId?: ObjectId;
}

type GeothermalQuote = Omit<GeothermalQuoteDocument, 'id' | 'accountId' | 'productId' | 'priceSheetMappingId'> & {
  id: string;
  accountId: string;
  productId: string;
  priceSheetMappingId: string;
};

interface EnergyBackupQuoteDocument {
  id: ObjectId;
  congaPackageId?: string;
  accountId: ObjectId;
  productId: ObjectId;
  externalReference?: string;
  type: FinanceType;
  productName: string;
  escalationRate: number;
  totalSystemCost: number;
  systemPricingDetails: EquipmentPricingDetails[];
  totalAmountPaid: number;
  status: QuoteStatus;
  priceSheetMappingId: ObjectId;
  endOfTermBuyoutPrice?: number;
  endOfTermBuyoutSchedule?: Array<{ month: number; amount: number }>;
}

type EnergyBackupQuote = Omit<
  EnergyBackupQuoteDocument,
  'id' | 'accountId' | 'productId' | 'priceSheetMappingId'
> & {
  id: string;
  accountId: string;
  productId: string;
  priceSheetMappingId: string;
};

export enum ConsumerSolarQuoteType {
  initial = 'initial',
  full = 'full',
}

export interface ConsumerSolarQuoteDocument extends SolarQuoteDocument {
  quoteType: ConsumerSolarQuoteType;
  /** Production factor = Y1 Production / System Size (kWh/kW) */
  productionFactor?: number;
  /** Low (<1000 kWh/kW) or High (>=1000 kWh/kW) */
  productionBand?: 'low' | 'high';
  /** Savings percentage vs ACP (30% for low band, 50% for high band) */
  savingsPercentage?: number;
  /** Average cost of power in customer's utility territory */
  averageCostOfPower?: number;
  /** EPC-facing $/W rate (set at Org transfer time) */
  epcRatePerWatt?: number;
  /** Whether the FICO-based max system size cap was checked and passed */
  systemSizeCapCheck?: boolean;
  /** Max system size (kW DC) allowed for this customer's FICO band */
  maxSystemSizeKwDc?: number;
  /** Customer's FICO band used for system size cap validation */
  ficoBandAtQuote?: string;
  /** Reference to the initial quote this full quote supersedes */
  supersededQuoteId?: ObjectId;
  /** Estimated sales tax amount */
  salesTaxAmount?: number;
  /** Sales tax rate applied */
  salesTaxRate?: number;
}

export interface CreateConsumerSolarQuoteRequest {
  productId: string;
  externalReference?: string;
  electricalUpgradeIncluded?: boolean;
  includeDomesticContentPricing?: boolean;
  battery?: BatteryDetails;
  consumerSolarQuoteType?: ConsumerSolarQuoteType;
}

export type CreateHVACQuote = Omit<HVACQuoteDocument, 'id'>;
export type CreateWaterHeaterQuote = Omit<WaterHeaterQuoteDocument, 'id'>;
export type CreateGeneratorQuote = Omit<GeneratorQuoteDocument, 'id'>;
export type CreateGeothermalQuote = Omit<GeothermalQuoteDocument, 'id'>;
export type CreateEnergyBackupQuote = Omit<EnergyBackupQuoteDocument, 'id'>;
export type CreateConsumerSolarQuote = Omit<ConsumerSolarQuoteDocument, 'id'>;

// Uses ObjectID types for Ids as they exist in the DB
export type QuoteDocument<T extends ProgramType = ProgramType.solar> = T extends ProgramType.hvac
  ? HVACQuoteDocument
  : T extends ProgramType.energyBackup
    ? EnergyBackupQuoteDocument
    : T extends ProgramType.waterHeater
      ? WaterHeaterQuoteDocument
      : T extends ProgramType.generator
        ? GeneratorQuoteDocument
        : T extends ProgramType.geothermal
          ? GeothermalQuoteDocument
          : T extends ProgramType.consumerSolar
            ? ConsumerSolarQuoteDocument
            : SolarQuoteDocument;

// Uses string types for Ids as they are returned from the API
export type Quote<T extends ProgramType = ProgramType.solar> = T extends ProgramType.hvac
  ? HVACQuote
  : T extends ProgramType.energyBackup
    ? EnergyBackupQuote
    : T extends ProgramType.waterHeater
      ? WaterHeaterQuote
      : T extends ProgramType.generator
        ? GeneratorQuote
        : T extends ProgramType.geothermal
          ? GeothermalQuote
          : SolarQuote;

export interface MonthlyPaymentV3 {
  year: number;
  kwhRate: number;
  monthlyPayment: number;
  estimatedAnnualProduction: number;
  guaranteedAnnualProduction: number;
  batteryPayment?: number;
  totalMonthlyPayment?: number;
}

export interface CreateCustomQuoteRequestV3 {
  kwhRate: number;
  firstYearMonthlyPayment: number;
  firstYearBatteryPayment?: number;
  pricePerWatt: number;
  escalationRate: number;
  externalReference?: string;
  electricalUpgradeIncluded?: boolean;
  monthlyPaymentSchedule?: MonthlyPaymentV3[];
  leaseDuration?: number;
  battery?: {
    type: BatteryType;
    pricingMethod: BatteryPricingMethodOption;
    cost?: number;
    manufacturer: string;
    models: {
      modelNumber: string;
      quantity: number;
    }[];
  };
  includeDomesticContentPricing?: boolean;
}
